import { useNavigate } from 'react-router-dom';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import {
  Building2,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Zap,
} from 'lucide-react';
import { useCompanyStatus } from '../../../hooks/useCompanyRegistration';

const TIMELINE_STEPS = [
  { key: 'DRAFT', label: 'Cadastro', icon: Building2 },
  { key: 'DOCUMENTS_UPLOADED', label: 'Documentos', icon: FileText },
  { key: 'PENDING_COMPLIANCE', label: 'Em Análise', icon: Clock },
  { key: 'APPROVED', label: 'Aprovado', icon: CheckCircle },
  { key: 'ACTIVE', label: 'Ativo', icon: Zap },
];

const STATUS_ORDER: Record<string, number> = {
  DRAFT: 0,
  DOCUMENTS_UPLOADED: 1,
  PENDING_COMPLIANCE: 2,
  APPROVED: 3,
  ACTIVE: 4,
  REJECTED: 2,
  PENDING_ADDITIONAL_DOCS: 2,
};

function formatDate(dateStr?: string) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function elapsedTime(dateStr?: string) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days} dia${days > 1 ? 's' : ''} e ${hours % 24}h`;
  return `${hours}h ${Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))}min`;
}

export function CompanyStatus() {
  const navigate = useNavigate();
  const { data: status, isLoading, error } = useCompanyStatus();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={32} className="animate-spin text-primary-400" />
      </div>
    );
  }

  if (error || !status) {
    return (
      <div className="space-y-6 max-w-3xl mx-auto">
        <h1 className="text-2xl font-bold text-white">Status da Empresa</h1>
        <Card>
          <div className="p-8 text-center space-y-4">
            <Building2 size={48} className="mx-auto text-surface-500" />
            <p className="text-surface-300">Nenhum cadastro de empresa encontrado.</p>
            <Button variant="primary" onClick={() => navigate('/banking/company/register')}>
              Iniciar Cadastro
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const currentStep = STATUS_ORDER[status.status] ?? 0;
  const isRejected = status.status === 'REJECTED';
  const isPendingDocs = status.status === 'PENDING_ADDITIONAL_DOCS';
  const isActive = status.status === 'ACTIVE';
  const isPending = status.status === 'PENDING_COMPLIANCE';

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-white">Status da Empresa</h1>
        <p className="text-surface-400 mt-1">Acompanhe o andamento do seu cadastro.</p>
      </div>

      {/* Active Badge */}
      {isActive && (
        <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30 flex items-center gap-3">
          <CheckCircle size={24} className="text-green-400" />
          <div>
            <p className="text-green-400 font-semibold text-lg">Conta Ativa</p>
            <p className="text-green-300 text-sm">
              Sua empresa está ativa! Você já pode realizar depósitos, saques e transferências.
            </p>
          </div>
        </div>
      )}

      {/* Rejection */}
      {isRejected && (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 space-y-3">
          <div className="flex items-start gap-3">
            <XCircle size={24} className="text-red-400 mt-0.5" />
            <div>
              <p className="text-red-400 font-semibold">Cadastro não aprovado</p>
              <p className="text-red-300 text-sm mt-1">
                {status.rejectionReason || 'Seu cadastro precisa de correções. Revise os dados e documentos e tente novamente.'}
              </p>
            </div>
          </div>
          <Button variant="primary" onClick={() => navigate('/banking/company/documents')}>
            <RefreshCw size={16} className="mr-2" /> Corrigir e Reenviar
          </Button>
        </div>
      )}

      {/* Pending Additional Docs */}
      {isPendingDocs && (
        <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30 space-y-3">
          <div className="flex items-start gap-3">
            <AlertTriangle size={24} className="text-yellow-400 mt-0.5" />
            <div>
              <p className="text-yellow-400 font-semibold">Documentos adicionais necessários</p>
              <p className="text-yellow-300 text-sm mt-1">
                São necessários documentos adicionais para completar a análise do seu cadastro.
              </p>
            </div>
          </div>
          <Button variant="primary" onClick={() => navigate('/banking/company/documents')}>
            <FileText size={16} className="mr-2" /> Enviar Documentos
          </Button>
        </div>
      )}

      {/* Pending Compliance */}
      {isPending && (
        <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
          <div className="flex items-start gap-3">
            <Clock size={24} className="text-blue-400 mt-0.5" />
            <div>
              <p className="text-blue-400 font-semibold">Cadastro em análise</p>
              <p className="text-blue-300 text-sm mt-1">
                Seu cadastro foi enviado para análise. A aprovação pode levar até 48 horas úteis.
              </p>
              {status.submittedAt && (
                <div className="mt-2 text-xs text-blue-300/70 space-y-1">
                  <p>Enviado em: {formatDate(status.submittedAt)}</p>
                  <p>Tempo decorrido: {elapsedTime(status.submittedAt)}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Timeline */}
      <Card>
        <div className="p-6">
          <h2 className="text-lg font-semibold text-white mb-6">Progresso</h2>
          <div className="relative">
            {TIMELINE_STEPS.map((step, idx) => {
              const isCompleted = currentStep > idx;
              const isCurrent = currentStep === idx && !isRejected;
              const StepIcon = step.icon;

              return (
                <div key={step.key} className="flex items-start gap-4 relative">
                  {/* Vertical line */}
                  {idx < TIMELINE_STEPS.length - 1 && (
                    <div className={`absolute left-5 top-10 w-0.5 h-8 ${isCompleted ? 'bg-primary-500' : 'bg-surface-700'}`} />
                  )}
                  {/* Icon */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isCompleted ? 'bg-primary-500 text-white' :
                    isCurrent ? 'bg-primary-500/20 text-primary-400 ring-2 ring-primary-500' :
                    'bg-surface-800 text-surface-500'
                  }`}>
                    <StepIcon size={18} />
                  </div>
                  {/* Label */}
                  <div className={`pb-8 ${isCurrent ? 'text-white' : isCompleted ? 'text-surface-300' : 'text-surface-500'}`}>
                    <p className="font-medium text-sm">{step.label}</p>
                    {isCurrent && (
                      <p className="text-xs text-primary-400 mt-0.5">Etapa atual</p>
                    )}
                    {isCompleted && (
                      <p className="text-xs text-surface-500 mt-0.5">Concluído</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      {/* History */}
      {status.history && status.history.length > 0 && (
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Histórico</h2>
            <div className="space-y-3">
              {status.history.map(entry => (
                <div key={entry.id} className="flex items-start gap-3 text-sm">
                  <div className="w-2 h-2 rounded-full bg-surface-600 mt-1.5 flex-shrink-0" />
                  <div>
                    <p className="text-surface-300">{entry.action.replace(/_/g, ' ')}</p>
                    <p className="text-xs text-surface-500">{formatDate(entry.changedAt)}</p>
                    {entry.notes && <p className="text-xs text-surface-400 mt-0.5">{entry.notes}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
